import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/field-label/sp-field-label.js';
import '@spectrum-web-components/help-text/sp-help-text.js';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/textfield/sp-textfield.js';

import '../../components/confirmation-dialog';
import { api } from '../../api/index.js';
import type {
  KlardatenCredentialsDto,
  KlardatenInstance,
  KlardatenProfile,
} from '../../api/klardaten-credentials.js';
import {
  getKlardatenInstanceLabel,
  getKlardatenProfilePrimaryLabel,
  getKlardatenProfileSecondaryLabel,
} from '../../utils/klardaten-display';
import {
  getNextInstanceDefault,
  getNextProfileDefault,
} from './klardaten-defaults';
import { settingsSharedStyles } from './settings-shared.styles';

const KLARDATEN_API_KEY_LENGTH = 64;
const KLARDATEN_API_KEY_PREFIX = 'uk-';

function isValidKlardatenApiKeyFormat(key: string): boolean {
  const trimmed = key.trim();
  return (
    trimmed.length === KLARDATEN_API_KEY_LENGTH &&
    trimmed.startsWith(KLARDATEN_API_KEY_PREFIX)
  );
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

@customElement('kv-settings-klardaten')
@localized()
export class KvSettingsKlardaten extends LitElement {
  static styles = [
    settingsSharedStyles,
    css`
      .key-summary {
        align-items: center;
        background: var(--settings-surface-muted);
        border: 1px solid var(--settings-border);
        border-radius: 8px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: space-between;
        margin-bottom: 16px;
        padding: 10px 12px;
      }

      .key-suffix,
      .meta-text {
        color: var(--color-app-text-secondary);
        font-size: 12px;
        font-weight: 600;
      }

      .delete-key-btn {
        margin-left: auto;
      }

      .defaults-grid {
        display: grid;
        gap: 16px;
      }

      sp-picker {
        width: 100%;
      }
    `,
  ];

  @state()
  accessor credentials: KlardatenCredentialsDto | null = null;

  @state()
  accessor apiKeyInput = '';

  @state()
  accessor loading = false;

  @state()
  accessor saving = false;

  @state()
  accessor testing = false;

  @state()
  accessor deleting = false;

  @state()
  accessor loadingDefaults = false;

  @state()
  accessor savingDefaults = false;

  @state()
  accessor message: { type: 'success' | 'error'; text: string } | null = null;

  @state()
  accessor instances: KlardatenInstance[] = [];

  @state()
  accessor profiles: KlardatenProfile[] = [];

  @state()
  accessor confirmDeleteCredentials = false;

  override connectedCallback() {
    super.connectedCallback();
    void this.loadCredentials();
  }

  private async loadCredentials() {
    this.loading = true;
    this.message = null;
    try {
      this.credentials = await api.klardatenCredentials.get();
      if (this.credentials?.hasKey) {
        try {
          await this.refreshDefaults();
        } catch (err: unknown) {
          this.message = {
            type: 'error',
            text: this.errorText(err, msg('Failed to load workflow defaults.')),
          };
        }
      }
    } catch {
      this.credentials = this.emptyCredentials();
    } finally {
      this.loading = false;
    }
  }

  private emptyCredentials(): KlardatenCredentialsDto {
    return {
      hasKey: false,
      last4: null,
      defaultClientInstanceId: null,
      defaultProfileId: null,
    };
  }

  private async patchDefaults(data: {
    defaultClientInstanceId?: string | null;
    defaultProfileId?: string | null;
  }) {
    this.savingDefaults = true;
    try {
      this.credentials = await api.klardatenCredentials.patch(data);
    } finally {
      this.savingDefaults = false;
    }
  }

  private async refreshDefaults() {
    if (!this.credentials?.hasKey) return;
    this.loadingDefaults = true;
    try {
      const instances = await api.klardatenCredentials.listInstances();
      this.instances = Array.isArray(instances) ? instances : [];

      const nextInstanceId = getNextInstanceDefault(
        this.instances,
        this.credentials.defaultClientInstanceId
      );
      if (nextInstanceId !== undefined) {
        await this.patchDefaults({
          defaultClientInstanceId: nextInstanceId,
          defaultProfileId: null,
        });
      }

      await this.refreshProfiles();
    } finally {
      this.loadingDefaults = false;
    }
  }

  private async refreshProfiles() {
    const instanceId = this.credentials?.defaultClientInstanceId;
    this.profiles = [];
    if (!instanceId) return;

    const profiles = await api.klardatenCredentials.listProfiles(instanceId);
    this.profiles = Array.isArray(profiles) ? profiles : [];
    const nextProfileId = getNextProfileDefault(
      this.profiles,
      this.credentials?.defaultProfileId
    );
    if (nextProfileId !== undefined) {
      await this.patchDefaults({ defaultProfileId: nextProfileId });
    }
  }

  private async save() {
    this.saving = true;
    this.message = null;
    try {
      const keyTrimmed = this.apiKeyInput.trim();
      if (!keyTrimmed) {
        this.message = this.credentials?.hasKey
          ? {
              type: 'error',
              text: msg(
                'To change the key, delete the key and create a new one.'
              ),
            }
          : {
              type: 'error',
              text: msg('Enter an API key to save.'),
            };
        return;
      }
      if (!isValidKlardatenApiKeyFormat(keyTrimmed)) {
        this.message = {
          type: 'error',
          text: msg(
            str`API key must be ${KLARDATEN_API_KEY_LENGTH} characters and start with "${KLARDATEN_API_KEY_PREFIX}".`
          ),
        };
        return;
      }
      await api.klardatenCredentials.post({
        apiKey: keyTrimmed,
      });
      this.credentials = await api.klardatenCredentials.get();
      this.apiKeyInput = '';
      await this.refreshDefaults();
      this.message = { type: 'success', text: msg('Settings saved.') };
    } catch (err: unknown) {
      this.message = {
        type: 'error',
        text: this.errorText(err, msg('Failed to save.')),
      };
    } finally {
      this.saving = false;
    }
  }

  private async testConnection() {
    this.testing = true;
    this.message = null;
    try {
      await this.refreshDefaults();
      this.message = {
        type: 'success',
        text: this.instances.length
          ? msg(str`Connection OK. Found ${this.instances.length} instance(s).`)
          : msg('Connection OK. No instances returned.'),
      };
    } catch (err: unknown) {
      this.message = {
        type: 'error',
        text: this.errorText(err, msg('Failed to fetch instances.')),
      };
    } finally {
      this.testing = false;
    }
  }

  private requestDeleteCredentials() {
    if (!this.credentials?.hasKey) return;
    this.confirmDeleteCredentials = true;
  }

  private cancelDeleteCredentials = () => {
    this.confirmDeleteCredentials = false;
  };

  private async deleteCredentials() {
    if (!this.credentials?.hasKey) return;
    this.confirmDeleteCredentials = false;
    this.deleting = true;
    this.message = null;
    try {
      await api.klardatenCredentials.delete();
      this.credentials = this.emptyCredentials();
      this.instances = [];
      this.profiles = [];
      this.apiKeyInput = '';
      this.message = { type: 'success', text: msg('API key removed.') };
    } catch (err: unknown) {
      this.message = {
        type: 'error',
        text: this.errorText(err, msg('Failed to remove API key.')),
      };
    } finally {
      this.deleting = false;
    }
  }

  private async onInstanceChange(e: Event) {
    const value = asString((e.target as { value?: unknown })?.value);
    if (value === (this.credentials?.defaultClientInstanceId ?? null)) return;

    const previousCredentials = this.credentials;
    if (this.credentials) {
      this.credentials = {
        ...this.credentials,
        defaultClientInstanceId: value,
        defaultProfileId: null,
      };
    }
    this.profiles = [];
    this.message = null;
    try {
      await this.patchDefaults({
        defaultClientInstanceId: value,
        defaultProfileId: null,
      });
      await this.refreshProfiles();
    } catch (err: unknown) {
      this.credentials = previousCredentials;
      this.message = {
        type: 'error',
        text: this.errorText(err, msg('Failed to save instance default.')),
      };
    }
  }

  private async onProfileChange(e: Event) {
    const value = asString((e.target as { value?: unknown })?.value);
    if (value === (this.credentials?.defaultProfileId ?? null)) return;

    const previousCredentials = this.credentials;
    if (this.credentials) {
      this.credentials = {
        ...this.credentials,
        defaultProfileId: value,
      };
    }
    this.message = null;
    try {
      await this.patchDefaults({ defaultProfileId: value });
    } catch (err: unknown) {
      this.credentials = previousCredentials;
      this.message = {
        type: 'error',
        text: this.errorText(err, msg('Failed to save profile default.')),
      };
    }
  }

  private onApiKeyInput(e: Event) {
    this.apiKeyInput = (e.target as HTMLInputElement)?.value ?? '';
  }

  private errorText(err: unknown, fallback: string): string {
    return err && typeof err === 'object' && 'message' in err
      ? String((err as { message: string }).message)
      : fallback;
  }

  private renderDefaultsPanel() {
    if (!this.credentials?.hasKey) return null;
    const selectedInstanceId = this.credentials.defaultClientInstanceId ?? '';
    const selectedProfileId = this.credentials.defaultProfileId ?? '';
    const defaultsDisabled =
      this.loadingDefaults || this.instances.length === 0;

    return html`
      <div class="settings-panel">
        <div class="settings-panel-header">
          <div>
            <h3 class="settings-panel-title">${msg('Workflow defaults')}</h3>
            <p class="settings-panel-description">
              ${msg(
                'DATEV nodes use these values when their instance or profile fields are empty.'
              )}
            </p>
          </div>
          ${
            this.loadingDefaults || this.savingDefaults
              ? html`<span class="meta-text">${msg('Updating...')}</span>`
              : ''
          }
        </div>

        <div class="defaults-grid">
          <div class="settings-form-row">
            <sp-field-label for="klardaten-instance">
              ${msg('Default instance')}
            </sp-field-label>
            <select
              class="settings-native-select"
              id="klardaten-instance"
              .value=${live(selectedInstanceId)}
              ?disabled=${defaultsDisabled}
              @change=${(e: Event) => void this.onInstanceChange(e)}
            >
              ${
                this.instances.length === 0
                  ? html`<option value="" disabled ?selected=${!selectedInstanceId}>
                      ${msg('No instances available')}
                    </option>`
                  : html`
                      <option value="" ?selected=${!selectedInstanceId}>
                        ${msg('Select an instance')}
                      </option>
                      ${this.instances.map((inst) => {
                        const instanceId = inst.id ?? '';
                        return html`
                        <option
                          value=${instanceId}
                          ?selected=${instanceId === selectedInstanceId}
                        >
                          ${getKlardatenInstanceLabel(inst)}
                        </option>
                      `;
                      })}
                    `
              }
            </select>
          </div>

          <div class="settings-form-row">
            <sp-field-label for="klardaten-profile">
              ${msg('Default profile')}
            </sp-field-label>
            <sp-picker
              id="klardaten-profile"
              label=${msg('Select a profile')}
              .value=${live(selectedProfileId)}
              ?disabled=${!selectedInstanceId || this.profiles.length === 0}
              @change=${(e: Event) => void this.onProfileChange(e)}
            >
              ${
                this.profiles.length === 0
                  ? html`<sp-menu-item value="">
                      ${msg('No profiles available')}
                    </sp-menu-item>`
                  : html`
                      <sp-menu-item value="">
                        ${msg('No default profile')}
                      </sp-menu-item>
                      ${this.profiles.map(
                        (profile) => html`
                        <sp-menu-item value=${profile.profile_id}>
                          ${getKlardatenProfilePrimaryLabel(profile)}
                          <span slot="description">
                            ${getKlardatenProfileSecondaryLabel(profile)}
                          </span>
                        </sp-menu-item>
                      `
                      )}
                    `
              }
            </sp-picker>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="settings-loading">
          <sp-progress-circle
            size="m"
            label=${msg('Loading Klardaten settings')}
            indeterminate
          ></sp-progress-circle>
        </div>
      `;
    }

    return html`
      <section class="settings-section">
        <header class="settings-header">
          <div class="settings-title-row">
            <h2 class="settings-title">Klardaten / DATEVconnect</h2>
          </div>
          <p class="settings-description">
            ${msg(
              'Connect your Klardaten account and choose defaults for DATEV workflow nodes.'
            )}
          </p>
        </header>

        <div class="settings-panel">
          <div class="settings-panel-header">
            <div>
              <h3 class="settings-panel-title">${msg('API connection')}</h3>
              <p class="settings-panel-description">
                ${msg('Store one key for Klardaten data access and connection testing.')}
              </p>
            </div>
          </div>

          <div class="key-summary">
            ${
              this.credentials?.hasKey
                ? html`
                    <span class="settings-chip success">${msg('API key saved')}</span>
                    <span class="key-suffix">uk-...${this.credentials.last4}</span>
                  `
                : html`<span class="settings-chip neutral">${msg('No API key saved')}</span>`
            }
          </div>

          <div class="settings-form-row">
            <sp-field-label for="klardaten-api-key">${msg('API key')}</sp-field-label>
            <sp-textfield
              id="klardaten-api-key"
              type="password"
              placeholder=${
                this.credentials?.hasKey
                  ? msg('Enter new key to replace, or delete key below')
                  : msg('Enter your Klardaten API key')
              }
              .value=${this.apiKeyInput}
              @input=${this.onApiKeyInput}
              maxlength=${KLARDATEN_API_KEY_LENGTH}
            ></sp-textfield>
            <sp-help-text>
              ${msg(
                str`Format: ${KLARDATEN_API_KEY_PREFIX}... (${KLARDATEN_API_KEY_LENGTH} characters total)`
              )}
            </sp-help-text>
          </div>

          <div class="settings-actions">
            <sp-button
              variant="accent"
              ?disabled=${this.saving || this.deleting}
              @click=${() => void this.save()}
            >
              ${this.saving ? msg('Saving...') : msg('Save')}
            </sp-button>
            <sp-button
              variant="secondary"
              ?disabled=${this.testing || this.deleting || !this.credentials?.hasKey}
              @click=${() => void this.testConnection()}
            >
              ${this.testing ? msg('Testing...') : msg('Test connection')}
            </sp-button>
            ${
              this.credentials?.hasKey
                ? html`
                  <sp-button
                    variant="secondary"
                    class="delete-key-btn settings-danger-button"
                    ?disabled=${this.saving || this.testing || this.deleting}
                    @click=${() => this.requestDeleteCredentials()}
                  >
                    ${this.deleting ? msg('Removing...') : msg('Delete API key')}
                  </sp-button>
                `
                : ''
            }
          </div>
        </div>

        ${this.renderDefaultsPanel()}

        ${
          this.message
            ? html`
              <div class="settings-message ${this.message.type}" role="status">
                ${this.message.text}
              </div>
            `
            : ''
        }
        ${
          this.confirmDeleteCredentials && this.credentials?.hasKey
            ? html`
              <kv-confirmation-dialog
                heading=${msg('Delete Klardaten API key')}
                confirm-label=${msg('Delete key')}
                .confirming=${this.deleting}
                @cancel=${this.cancelDeleteCredentials}
                @confirm=${() => void this.deleteCredentials()}
              >
                <p>
                  ${msg(
                    str`Delete API key uk-...${this.credentials.last4 ?? ''}? Klardaten and DATEV workflow nodes will lose access until a new key is saved, and workflow defaults will be cleared.`
                  )}
                </p>
              </kv-confirmation-dialog>
            `
            : ''
        }
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-settings-klardaten': KvSettingsKlardaten;
  }
}
